/*
 * This class is copied verbatim from Backstage 1.39.1.
 */

import { InfoCard } from '@mainstage/core-components';
import { discoveryApiRef, useApi } from '@mainstage/core-plugin-api';
import { useTranslationRef } from '@mainstage/core-plugin-api/alpha';
import { ResponseError } from '@mainstage/errors';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import useLocalStorage from 'react-use/esm/useLocalStorage';
import { ProxiedSignInIdentity } from '../ProxiedSignInPage/ProxiedSignInIdentity';
import { coreComponentsTranslationRef } from '../translation';
import { GuestUserIdentity } from './GuestUserIdentity';
import { GridItem } from './styles';
import { ProviderComponent, ProviderLoader, SignInProvider } from './types';

const getIdentity = async (identity: ProxiedSignInIdentity) => {
  try {
    const identityResponse = await identity.getBackstageIdentity();
    return identityResponse;
  } catch (error) {
    if (
      error.name === 'ResponseError' &&
      (error as ResponseError).cause.name === 'NotFoundError'
    ) {
      return undefined;
    }
    throw error;
  }
};

const Component: ProviderComponent = ({
  onSignInStarted,
  onSignInSuccess,
  onSignInFailure,
}) => {
  const discoveryApi = useApi(discoveryApiRef);
  const [_, setUseLegacyGuestToken] = useLocalStorage('enableLegacyGuestToken');
  const { t } = useTranslationRef(coreComponentsTranslationRef);

  const handle = async () => {
    onSignInStarted();

    const identity = new ProxiedSignInIdentity({
      provider: 'guest',
      discoveryApi,
    });

    const identityResponse = await getIdentity(identity).catch(error => {
      // eslint-disable-next-line no-console
      console.warn(`Failed to sign in as a guest, ${error}`);
      return undefined;
    });

    if (!identityResponse) {
      // eslint-disable-next-line no-alert
      const useLegacyGuestTokenResponse = confirm(
        'Failed to sign in as a guest using the auth backend. Do you want to fallback to the legacy guest token?',
      );
      if (useLegacyGuestTokenResponse) {
        setUseLegacyGuestToken(true);
        onSignInSuccess(new GuestUserIdentity());
        return;
      }
      onSignInFailure();
      throw new Error(
        `You cannot sign in as a guest, you must either enable the legacy guest token or configure the auth backend to support guest sign in.`,
      );
    }

    onSignInSuccess(identity);
  };

  return (
    <GridItem>
      <InfoCard
        title="Guest"
        variant="fullHeight"
        actions={
          <Button color="primary" variant="outlined" onClick={handle}>
            {t('signIn.guestProvider.enter')}
          </Button>
        }
      >
        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
          {t('signIn.guestProvider.subtitle')}
        </Typography>
      </InfoCard>
    </GridItem>
  );
};

const loader: ProviderLoader = async apis => {
  const useLegacyGuestToken =
    localStorage.getItem('enableLegacyGuestToken') === 'true';

  const identity = new ProxiedSignInIdentity({
    provider: 'guest',
    discoveryApi: apis.get(discoveryApiRef)!,
  });
  const identityResponse = await getIdentity(identity).catch(error => {
    // eslint-disable-next-line no-console
    console.warn(`Failed to sign in as a guest, ${error}`);
    return undefined;
  });

  if (!identityResponse && !useLegacyGuestToken) {
    return undefined;
  } else if (identityResponse && useLegacyGuestToken) {
    // eslint-disable-next-line no-alert
    const switchToNewGuestToken = confirm(
      'You are currently using the legacy guest token, but you have the new guest backend module installed. Do you want to use the new module?',
    );
    if (switchToNewGuestToken) {
      localStorage.removeItem('enableLegacyGuestToken');
    } else {
      return new GuestUserIdentity();
    }
  } else if (useLegacyGuestToken) {
    return new GuestUserIdentity();
  }

  return identity;
};

export const guestProvider: SignInProvider = { Component, loader };
